import { useEffect, useState } from 'react';
import { DEFAULT_POSITION_BUDGET_MS, reviewGame } from '../../../games/analysis/review.ts';
import type { MoveReview, SeatTally } from '../../../games/analysis/review.ts';
import type { SeatOf } from '../../../games/spec/interpreter.ts';
import type { RulesSpec } from '../../../games/spec/schema.ts';
import type { AnalyseRequest, AnalysisResponse } from './protocol.ts';

export interface AnalysisInput {
  readonly rules: RulesSpec;
  readonly firstSeat: number;
  /** Board moves only, in play order. */
  readonly params: readonly number[];
  /** Engine thinking time per move. Lower it to keep tests quick. */
  readonly budgetMs?: number;
}

export interface AnalysisState {
  /** Finished plies, in order. Grows while `status` is `'running'`. */
  readonly entries: readonly MoveReview[];
  /** How many plies there are to do, so progress can be stated as "12 of 38". */
  readonly total: number;
  readonly tallies: readonly SeatTally[];
  readonly status: 'idle' | 'running' | 'ready' | 'failed';
  /** Set only when `status` is `'failed'`. The screen says so and stays usable. */
  readonly error: string | null;
}

const IDLE: AnalysisState = {
  entries: [],
  total: 0,
  tallies: [],
  status: 'idle',
  error: null,
};

/**
 * Run the engine over a finished game.
 *
 * In a browser this is a worker, and results stream in ply by ply. Where there
 * is no `Worker` — jsdom, and any environment that has disabled them — it falls
 * back to running the same pure function inline. The fallback blocks, which is
 * exactly why it is the fallback and not the plan.
 *
 * Passing `null` (no match loaded yet, or a match with no moves) parks the hook
 * in `'idle'` rather than analysing an empty game.
 */
export function useAnalysis(input: AnalysisInput | null): AnalysisState {
  const [state, setState] = useState<AnalysisState>(IDLE);

  const rules = input?.rules ?? null;
  const firstSeat = input?.firstSeat ?? 0;
  const budgetMs = input?.budgetMs ?? DEFAULT_POSITION_BUDGET_MS;
  // The move list is the identity of the work: a new array with the same moves
  // must not restart a two-second analysis on every render.
  const paramsKey = input === null ? '' : input.params.join(',');

  useEffect(() => {
    if (rules === null || paramsKey === '') {
      setState(IDLE);
      return;
    }

    const params = paramsKey.split(',').map(Number);
    const total = params.length;
    setState({ entries: [], total, tallies: [], status: 'running', error: null });

    let live = true;
    const entries: MoveReview[] = [];

    const request: AnalyseRequest = {
      kind: 'analyse',
      rules,
      firstSeat,
      params,
      budgetMs,
    };

    if (typeof Worker === 'undefined') {
      // Yield one frame first, so the "Analysing…" state actually paints before
      // the main thread disappears into the search.
      const timer = setTimeout(() => {
        if (!live) return;
        try {
          const review = reviewGame(
            rules,
            firstSeat as SeatOf<RulesSpec>,
            params.map((param) => ({ param })),
            { budgetMs },
          );
          if (!live) return;
          setState({
            entries: review.entries,
            total,
            tallies: review.tallies,
            status: 'ready',
            error: null,
          });
        } catch (error) {
          if (!live) return;
          setState({
            entries: [],
            total,
            tallies: [],
            status: 'failed',
            error: error instanceof Error ? error.message : 'analysis failed',
          });
        }
      }, 0);
      return () => {
        live = false;
        clearTimeout(timer);
      };
    }

    const worker = new Worker(new URL('./analysis.worker.ts', import.meta.url), {
      type: 'module',
    });

    worker.addEventListener('message', (event: MessageEvent<AnalysisResponse>) => {
      if (!live) return;
      const message = event.data;
      if (message.kind === 'progress') {
        entries.push(message.entry);
        setState({
          entries: [...entries],
          total: message.total,
          tallies: [],
          status: 'running',
          error: null,
        });
        return;
      }
      if (message.kind === 'done') {
        setState({
          entries: message.review.entries,
          total: message.review.entries.length,
          tallies: message.review.tallies,
          status: 'ready',
          error: null,
        });
        return;
      }
      setState({
        entries: [],
        total,
        tallies: [],
        status: 'failed',
        error: message.message,
      });
    });

    worker.addEventListener('error', (event) => {
      if (!live) return;
      setState({
        entries: [],
        total,
        tallies: [],
        status: 'failed',
        error: event.message || 'the analysis worker stopped',
      });
    });

    worker.postMessage(request);

    return () => {
      live = false;
      // Nothing to salvage from a half-finished game: terminate rather than
      // leave a search burning a core behind a screen nobody is looking at.
      worker.terminate();
    };
  }, [rules, firstSeat, paramsKey, budgetMs]);

  return state;
}
